import * as THREE from 'three';
import { normalizeSceneryAssetId } from './production.ts';

type MaterialFamily = 'foliage' | 'wood' | 'metal' | 'stone' | 'accent' | 'path';

const COLORS: Record<MaterialFamily, number> = {
  foliage: 0x3f835d,
  wood: 0x7c563a,
  metal: 0x385467,
  stone: 0x7b8583,
  accent: 0xe7bd54,
  path: 0xc8b992,
};

function material(family: MaterialFamily): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: COLORS[family],
    roughness: family === 'metal' ? 0.5 : 0.82,
    metalness: family === 'metal' ? 0.52 : 0,
  });
}

function mesh(geometry: THREE.BufferGeometry, family: MaterialFamily, x = 0, y = 0, z = 0) {
  const result = new THREE.Mesh(geometry, material(family));
  result.position.set(x, y, z);
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

function box(
  root: THREE.Group,
  size: readonly [number, number, number],
  family: MaterialFamily,
  position: readonly [number, number, number],
) {
  root.add(mesh(new THREE.BoxGeometry(...size), family, ...position));
}

function post(
  root: THREE.Group,
  x: number,
  z: number,
  height: number,
  family: MaterialFamily = 'metal',
) {
  root.add(mesh(new THREE.CylinderGeometry(0.08, 0.1, height, 8), family, x, height / 2, z));
}

function pavingTile(root: THREE.Group, width: number, depth: number, x: number, z: number) {
  // The plot top reaches 0.30 m above groundElevation. Keep the paving
  // visibly proud of that surface instead of centering it inside the ground.
  box(root, [width - 0.08, 0.18, depth - 0.08], 'path', [x, 0.4, z]);
}

export function createProductionSceneryVisual(assetId: string): THREE.Group {
  const id = normalizeSceneryAssetId(assetId);
  const root = new THREE.Group();
  root.name = `production-scenery:${id}`;
  root.userData.assetId = id;

  if (id === 'broadleaf_tree' || id === 'conifer') {
    root.add(mesh(new THREE.CylinderGeometry(0.18, 0.32, 2.7, 8), 'wood', 0, 1.35, 0));
    root.add(
      mesh(
        new THREE.ConeGeometry(
          id === 'conifer' ? 1.45 : 1.8,
          id === 'conifer' ? 5.2 : 3.5,
          id === 'conifer' ? 10 : 12,
        ),
        'foliage',
        0,
        id === 'conifer' ? 4.5 : 4.1,
        0,
      ),
    );
  } else if (id === 'shrub_cluster') {
    [
      [-0.7, 0],
      [0.5, -0.35],
      [0.55, 0.55],
    ].forEach(([x, z], index) =>
      root.add(mesh(new THREE.DodecahedronGeometry(0.75 - index * 0.08), 'foliage', x!, 0.62, z!)),
    );
  } else if (id === 'hedge') box(root, [3.5, 1.5, 1.2], 'foliage', [0, 0.75, 0]);
  else if (id === 'rock_cluster') {
    [
      [-0.65, 0.25, 0.85],
      [0.45, 0, 1.05],
      [0.25, 0.6, 0.62],
    ].forEach(([x, z, s]) => {
      const rock = mesh(new THREE.DodecahedronGeometry(s!, 0), 'stone', x!, s! * 0.62, z!);
      rock.rotation.set(0.1, x!, 0.08);
      root.add(rock);
    });
  } else if (id === 'flower_bed') {
    box(root, [3, 0.25, 1.5], 'wood', [0, 0.12, 0]);
    [-1, -0.35, 0.35, 1].forEach((x, index) =>
      root.add(
        mesh(new THREE.SphereGeometry(0.22, 8, 6), index % 2 ? 'accent' : 'foliage', x, 0.46, 0),
      ),
    );
  } else if (id === 'planter') {
    box(root, [1.8, 0.7, 1.8], 'stone', [0, 0.35, 0]);
    root.add(mesh(new THREE.SphereGeometry(0.72, 10, 8), 'foliage', 0, 1.05, 0));
  } else if (id === 'path_straight' || id === 'path_corner' || id === 'plaza') {
    if (id === 'path_straight') {
      pavingTile(root, 2, 2, -1, 0);
      pavingTile(root, 2, 2, 1, 0);
    } else if (id === 'path_corner') {
      pavingTile(root, 4, 2, 0, -1);
      pavingTile(root, 2, 2, -1, 1);
    } else {
      pavingTile(root, 2, 4, -1, 0);
      pavingTile(root, 2, 4, 1, 0);
    }
  } else if (id === 'bench') {
    box(root, [2.4, 0.18, 0.65], 'wood', [0, 0.72, 0]);
    box(root, [2.4, 0.85, 0.16], 'wood', [0, 1.15, 0.3]);
    post(root, -0.9, 0, 0.72);
    post(root, 0.9, 0, 0.72);
  } else if (id === 'picnic_table') {
    box(root, [2.5, 0.18, 1.05], 'wood', [0, 1, 0]);
    box(root, [2.5, 0.15, 0.45], 'wood', [0, 0.62, -1]);
    box(root, [2.5, 0.15, 0.45], 'wood', [0, 0.62, 1]);
    post(root, -0.75, 0, 1);
    post(root, 0.75, 0, 1);
  } else if (id === 'bin') {
    root.add(mesh(new THREE.CylinderGeometry(0.38, 0.42, 1, 10), 'metal', 0, 0.5, 0));
    root.add(mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.1, 10), 'accent', 0, 1.02, 0));
  } else if (id === 'lamp') {
    post(root, 0, 0, 3.7);
    const globe = mesh(new THREE.SphereGeometry(0.32, 10, 8), 'accent', 0, 3.85, 0);
    (globe.material as THREE.MeshStandardMaterial).emissive.setHex(0xffaa44);
    (globe.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.55;
    root.add(globe);
  } else if (id === 'directional_sign') {
    post(root, 0, 0, 2.4);
    box(root, [1.8, 0.52, 0.12], 'accent', [0.35, 2.1, 0]);
  } else if (id === 'fence_straight' || id === 'fence_corner' || id === 'queue_rail') {
    const span = id === 'queue_rail' ? 3 : 4;
    const railHeight = id === 'queue_rail' ? 1.05 : 1.35;
    post(root, -span / 2, 0, railHeight);
    post(root, span / 2, 0, railHeight);
    box(root, [span, 0.1, 0.1], 'metal', [0, railHeight * 0.72, 0]);
    box(root, [span, 0.1, 0.1], 'metal', [0, railHeight, 0]);
    if (id === 'fence_corner') {
      post(root, 0, 2, railHeight);
      box(root, [0.1, 0.1, 4], 'metal', [0, railHeight, 1]);
      box(root, [0.1, 0.1, 4], 'metal', [0, railHeight * 0.72, 1]);
    }
  } else if (id === 'canopy') {
    [
      [-1.7, -1.2],
      [1.7, -1.2],
      [-1.7, 1.2],
      [1.7, 1.2],
    ].forEach(([x, z]) => post(root, x!, z!, 2.8));
    const roof = mesh(
      new THREE.CylinderGeometry(2.2, 2.2, 3.8, 4, 1, false, Math.PI / 4),
      'accent',
      0,
      3,
      0,
    );
    roof.scale.z = 0.65;
    root.add(roof);
  } else if (id === 'kiosk') {
    box(root, [3.3, 2.3, 2.8], 'wood', [0, 1.15, 0]);
    box(root, [3.7, 0.25, 3.2], 'accent', [0, 2.55, 0]);
    box(root, [2, 0.8, 0.12], 'metal', [0, 1.35, 1.46]);
  }
  return root;
}

export function disposeSceneryVisual(root: THREE.Object3D): void {
  if (root.userData.sharedProductionAsset) return;
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.geometry.dispose();
    if (Array.isArray(object.material)) object.material.forEach((entry) => entry.dispose());
    else object.material.dispose();
  });
}
